/**
 * Document tab strip — one strip per editor group. Ported from pik's
 * `.canvas-document-tabs` bar: click to activate, drag to reorder or tear
 * out, × to close (through the app's close request), horizontal wheel
 * scrolling, active-tab auto-reveal, plus the suite's opt-in capabilities
 * (dirty dot, pin glyph, middle-click close, overflow menu).
 */

import { Fragment, useEffect, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import type { DocTab, DocumentWorkspace } from '../types';
import type { WorkspaceInteractions } from './useWorkspaceInteractions';
import { PinIcon, ChevronDownIcon } from './icons';

const firstClass = (value: string | undefined): string =>
  (value ?? '').split(' ')[0];

export interface DocumentTabStripProps {
  groupId: string;
  tabs: DocTab[];
  activeTabId: string | null;
  draggingTabId: string | null;
  tabDropIndex: number | null;
  dockPreviewTab: { id: string; name: string } | null;
  interactions: WorkspaceInteractions;
  workspace: DocumentWorkspace;
  onCloseRequest: (id: string) => void;
  onActivate: (id: string) => void;
  noDocumentsLabel: string;
  registerRef: (groupId: string, el: HTMLDivElement | null) => void;
  /** App-supplied per-tab context menu (right-click); the strip itself stays menu-free */
  onTabContextMenu?: (tabId: string, event: ReactMouseEvent) => void;
}

export const DocumentTabStrip = ({
  groupId,
  tabs,
  activeTabId,
  draggingTabId,
  tabDropIndex,
  dockPreviewTab,
  interactions,
  workspace,
  onCloseRequest,
  onActivate,
  noDocumentsLabel,
  registerRef,
  onTabContextMenu,
}: DocumentTabStripProps) => {
  const { classNames: cn, features, labels } = workspace;
  const stripRef = useRef<HTMLDivElement | null>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const isDockTarget = dockPreviewTab !== null;
  const showIndicator = (index: number) =>
    isDockTarget && tabDropIndex === index && (draggingTabId !== null || dockPreviewTab !== null);

  const previewTabElement =
    isDockTarget && dockPreviewTab ? (
      <div key={`preview-${dockPreviewTab.id}`} className={`${cn.tab} ${cn.tabDockPreview}`}>
        <span className={cn.tabLabel}>{dockPreviewTab.name}</span>
      </div>
    ) : null;

  // Wheel over the strip scrolls it horizontally (non-passive so the default
  // vertical page scroll can be prevented)
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const handleWheel = (event: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;
      event.preventDefault();
      el.scrollLeft += event.deltaY;
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  // Keep the active tab visible in its strip
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const activeTab = el.querySelector<HTMLElement>(
      `.${firstClass(cn.tab)}.${firstClass(cn.tabActive)}`,
    );
    activeTab?.scrollIntoView({
      behavior: 'auto',
      inline: 'nearest',
      block: 'nearest',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId, tabs.length]);

  // Overflow measurement for the opt-in overflow menu
  useEffect(() => {
    if (!features.overflowMenu) return;
    const el = stripRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => setOverflowing(el.scrollWidth > el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [features.overflowMenu, tabs.length]);

  // Dismiss the overflow menu on any outside pointerdown or Escape
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (stripRef.current && target && !stripRef.current.contains(target)) setMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  return (
    <div
      data-doc-tab-strip
      className={cn.tabStrip}
      ref={(el) => {
        stripRef.current = el;
        registerRef(groupId, el);
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
    >
      {tabs.length === 0 && !previewTabElement ? (
        <span className={cn.emptyTabsHint}>{noDocumentsLabel}</span>
      ) : (
        <>
          {tabs.map((tab, index) => (
            <Fragment key={tab.id}>
              {showIndicator(index) && <div className={cn.dropIndicator} />}
              {isDockTarget && dockPreviewTab !== null && tabDropIndex === index && previewTabElement}
              <div
                data-doc-tab
                role="button"
                tabIndex={0}
                className={`${cn.tab} ${tab.id === activeTabId ? cn.tabActive : ''} ${tab.id === draggingTabId ? cn.tabDragging : ''}`}
                onClick={() => onActivate(tab.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onActivate(tab.id);
                  }
                }}
                onPointerDown={interactions.handleTabPointerDown(tab.id, groupId)}
                onContextMenu={
                  onTabContextMenu
                    ? (event) => {
                        event.preventDefault();
                        onTabContextMenu(tab.id, event);
                      }
                    : undefined
                }
                onAuxClick={(event) => {
                  if (!features.middleClickClose || event.button !== 1) return;
                  event.preventDefault();
                  onCloseRequest(tab.id);
                }}
              >
                {features.pinning && tab.isPinned && <PinIcon className={cn.tabPin} />}
                {features.dirtyDot && tab.isDirty && (
                  <span className={cn.tabDirty} aria-label="Unsaved changes" />
                )}
                <span className={cn.tabLabel}>{tab.label}</span>
                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    onCloseRequest(tab.id);
                  }}
                  aria-label={labels.closeTabAria(tab.label)}
                >
                  ×
                </button>
              </div>
            </Fragment>
          ))}
          {showIndicator(tabs.length) && <div className={cn.dropIndicator} />}
          {isDockTarget &&
            dockPreviewTab !== null &&
            (tabDropIndex === null || tabDropIndex >= tabs.length) &&
            previewTabElement}
        </>
      )}
      {features.overflowMenu && overflowing && (
        <button
          data-doc-overflow-button
          className={cn.overflowButton}
          onClick={() => setMenuOpen((open) => !open)}
          aria-label={labels.overflowLabel}
          title={labels.overflowLabel}
        >
          <ChevronDownIcon />
        </button>
      )}
      {features.overflowMenu && menuOpen && overflowing && (
        <div data-doc-overflow-menu className={cn.overflowMenu} role="menu">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="menuitem"
              className={`${cn.overflowItem} ${tab.id === activeTabId ? cn.overflowItemActive : ''}`}
              onClick={() => {
                setMenuOpen(false);
                onActivate(tab.id);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
